import React, { useEffect, useState } from "react";
import { Select, Spin, Table, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { useGetVolumesOutliersMutation } from "../../../store/api/lumi/outliers";
import { getSentimentStatus, SENTIMENT_STATUS } from "../../../utils/sentimentStatus";
import "./OutlierWidgetStyles.css";
import classes from "../BiggestChangeWidget/BiggestChangeWidget.module.css";
import { ShowCountOptions } from "../../../constants";
import moment from "moment/moment";
import { useSelector } from "react-redux";
import { getConceptFilter } from "../../../utils/getConceptFilter";

const { Option } = Select;

const COLORS = {
    Positive: "rgb(120, 208, 239)",
    Neutral: "#BDBCC8",
    Negative: "rgb(245, 150, 148)",
};
export function OutlierWidgetPercentage(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState(false);
    const [selectedConceptList, setSelectedConceptList] = useState(
        props.conceptList || "active",
    );
    const [conceptChangesCount, setConceptChangesCount] = useState(5);
    const [getOutlier, { isLoading: queryLoading, isError, error }] =
        useGetVolumesOutliersMutation();
    const metaDataDate = props.metaDataQu?.metaDataDate;
    const [selectedDateName, setSelectedDateName] = useState(
        props.widget.dataProps
            ? props.widget.dataProps.selectedDateName
            : metaDataDate[0]?.name || "",
    );
    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey]  || 16;
    });
    const [interval, setIntervalDate] = useState("year");

    async function getOutliers() {

        const conceptFilter = getConceptFilter(props);

        const filters = [
            {
                name: selectedDateName,
                minimum: getPastDateFromInterval(interval),
                maximum: moment().toISOString(),
            },
        ];
        const sentimentStatus = getSentimentStatus(props.project);
        const sentimentDisabled = sentimentStatus !== SENTIMENT_STATUS.READY;
        const response = await getOutlier({
            id: props.id,
            filter: filters,
            concept_selector: conceptFilter,
            match_type: props.selectedType === "match_count" ? "both" : "exact",
            conceptListId: selectedConceptList || props.conceptList || "active",
            scoreField: props.selectedFieldDriverName
            ,
            sentimentDisabled,
            limit: conceptChangesCount,
        });
        setData(response.data);
        props.setFilterDocCount(response?.data?.volumes?.filter_count);
    }

    useEffect(() => {
        setIsLoading(true);
        const debouncedGetOutliers = debounce(() => {
            getOutliers().finally(() => setIsLoading(false));
        }, 500);

        debouncedGetOutliers();

        return () => {
            debouncedGetOutliers.cancel();
        };
    }, [
        selectedConceptList,
        conceptChangesCount,
        interval,
        props.project,
        props.filters,
        props.selectedType,
        props.selectedFieldDriverName,
        props.conceptList,
        props.conceptTypeDay,
    ]);

    useEffect(() => {
        props.setAdditionalFilters([{
            key: "Special",
            label: "Time Filter:",
            showArrow: false,
            children:
                <SpecialFilter
                    conceptChangesCount={conceptChangesCount}
                    interval={interval}
                    setConceptChangesCount={setConceptChangesCount}
                    setIntervalDate={setIntervalDate}
                    selectedDateName={selectedDateName}
                    setSelectedDateName={setSelectedDateName}
                    metaDataDate={metaDataDate}
                    props={props}
                    selectedConceptList={selectedConceptList}
                    setSelectedConceptList={setSelectedConceptList}
                />,
        }]);
    }, [selectedConceptList,conceptChangesCount,selectedDateName,interval,props.dataList]);

    const consolidatedData = data?.volumes?.match_counts.map(
        (volume, index) => {
            const sentiment = data.sentiment?.match_counts?.find(
                (s) => s.name === volume.name,
            );
            const driver = data.drivers?.find(
                (d) => d.name === volume.name,
            );
            return {
                key: index,
                concept: volume.name,
                matchCount: volume.match_count,
                sentiment: sentiment
                    ? {
                        positive: sentiment.sentiment_share.positive,
                        negative: sentiment.sentiment_share.negative,
                        neutral: sentiment.sentiment_share.neutral,
                    }
                    : null,
                impact: driver
                    ? `${driver.average_score.toFixed(3)} (${driver.impact.toFixed(3)})`
                    : "N/A",
            };
        },
    );
    const renderSentimentBar = (sentiment) => {
        if (!sentiment) return "N/A";

        const total = sentiment.positive + sentiment.negative + sentiment.neutral;

        const positiveWidth = (sentiment.positive / total) * 100;
        const neutralWidth = (sentiment.neutral / total) * 100;
        const negativeWidth = (sentiment.negative / total) * 100;

        const barStyle = {
            display: 'flex',
            height: '15px',
        };

        const positiveStyle = {
            backgroundColor: COLORS.Positive,
            width: `${positiveWidth}%`,
        };

        const neutralStyle = {
            backgroundColor: COLORS.Neutral,
            width: `${neutralWidth}%`,
        };

        const negativeStyle = {
            backgroundColor: COLORS.Negative,
            width: `${negativeWidth}%`,
        };

        return (
            <Tooltip
                title={` Negative: ${(sentiment.negative * 100).toFixed(2)}%, Neutral: ${(sentiment.neutral * 100).toFixed(2)}%, Positive: ${(sentiment.positive * 100).toFixed(2)}% `}
            >
                <div style={barStyle}>
                    <div style={negativeStyle} />
                    <div style={neutralStyle} />
                    <div style={positiveStyle} />
                </div>
            </Tooltip>
        );
    };


    const renderImpact = (impact) => {
        if (impact.includes("N/A")) return "N/A";
        const [averageScore, impactScore] = impact
            .replace(/[()]/g, "")
            .split(" ");
        const impactValue = parseFloat(impactScore);
        const formattedImpact =
            impactValue > 0
                ? `+${impactValue.toFixed(3)}`
                : impactValue.toFixed(3);
        const color = impactValue >= 0 ? "green" : "red";

        return (
            <div
                style={{ color }}
            >{`${averageScore} (${formattedImpact})`}</div>
        );
    };

    const renderMatchCount = (matchCount) => {
        if (!props.totalDocCount) return <div>{matchCount}</div>;

        const percentage = ((matchCount / props.totalDocCount) * 100).toFixed(
            2,
        );
        return <div style={{fontSize: labelFontSize}}>{`${matchCount} (${percentage}%)`}</div>;
    };

    const columns = [
        {
            title: <div style={{ fontSize: labelFontSize }}  onMouseDown={e => e.stopPropagation()}>Concept</div>,
            dataIndex: "concept",
            key: "concept",
            align: "center",
            sorter: (a, b) => a.concept.localeCompare(b.concept),
            render: (text) => <div style={{ fontSize: labelFontSize }}>{text}</div>,
        },
        {
            title: <div style={{ fontSize: labelFontSize }}  onMouseDown={e => e.stopPropagation()}>Match Count</div>,
            dataIndex: "matchCount",
            key: "matchCount",
            align: "center",
            sorter: (a, b) => a.matchCount - b.matchCount,
            defaultSortOrder: "descend",
            render: renderMatchCount,
        },
        {
            title: <div style={{ fontSize: labelFontSize }}  onMouseDown={e => e.stopPropagation()}>Sentiment</div>,
            dataIndex: "sentiment",
            key: "sentiment",
            align: "center",
            render: renderSentimentBar,
        },
        {
            title: <div style={{ fontSize: labelFontSize }}  onMouseDown={e => e.stopPropagation()}>Drivers</div>,
            dataIndex: "impact",
            key: "impact",
            align: "center",
            render: renderImpact,
        },
    ];



    function getPastDateFromInterval(interval) {
        const today = moment();
        switch (interval) {
            case "week":
                return today.subtract(7, "days").toISOString();
            case "month":
                return today.subtract(30, "days").toISOString();
            case "quarter":
                return today.subtract(90, "days").toISOString();
            case "year":
                return today.subtract(365, "days").toISOString();
            default:
                console.error("Invalid interval:", interval);
                return null;
        }
    }

    if (isLoading || queryLoading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    if (isError) {
        return (
            <div>
                Error: {error?.data?.message || "An unexpected error occurred"}
            </div>
        );
    }


    return (
        <div className="skt-container" style={{ width: "100%%" }}>
            <div>
                <Table
                    dataSource={consolidatedData}
                    columns={columns}
                    pagination={false}
                    className="custom-table-style"
                    rowClassName="custom-table-row"
                    headerClassName="custom-table-header"
                    scroll={{ y: props.widget.layout.h * 39 - 200 }}
                />
            </div>
        </div>
    );
}


const SpecialFilter = ({
                           props,
                           conceptChangesCount,
                           setConceptChangesCount,
                           selectedDateName,
                           metaDataDate,
                           setIntervalDate,
                           interval,
                           setSelectedDateName,
                           selectedConceptList,
                           setSelectedConceptList,
                       }) => {

    const intervalOptions = [
        { label: "Previous 365 days", value: "year" },
        { label: "Previous 90 days", value: "quarter" },
        { label: "Previous 30 days", value: "month" },
        { label: "Previous 7 days", value: "week" },
    ];

    return (
        <div onMouseDown={e => e.stopPropagation()}
             className={classes.select_container}
             style={{ paddingBottom: "10px" }}
        >
            <div className={classes.select}>
                <label>Show Count</label>
                <Select
                    showSearch
                    style={{ width: 100 }}
                    placeholder="Show Count"
                    value={conceptChangesCount}
                    onChange={(value) => {
                        setConceptChangesCount(value);
                    }}
                    options={ShowCountOptions}
                    size="small"
                />
            </div>
            <div
                className={classes.select}
                style={{ display: "flex", alignItems: "center" }}
            >
                <label>Over Time Field</label>
                <Select
                    showSearch
                    style={{ width: 150 }}
                    placeholder="Select Filter Date Field"
                    value={
                        selectedDateName
                            ? props.selectedDateName
                            : metaDataDate[0].name
                    }
                    onChange={(value) => {
                        setSelectedDateName(value);
                    }}
                    options={metaDataDate.map((date) => ({
                        label: date.name,
                        value: date.name,
                    }))}
                    size="small"
                />
                <div
                    className={classes.select}
                    style={{ alignItems: "center" }}
                >
                    <label>Select time Interval</label>
                    <Select
                        showSearch
                        style={{ width: 150 }}
                        placeholder="Select Time Interval"
                        value={interval}
                        onChange={(value) => {
                            setIntervalDate(value);
                        }}
                        options={intervalOptions}
                        size="small"
                    />
                </div>
            </div>
            <div className={classes.select}>
                <label>Concept Lists</label>
                <Select
                    size={"small"}
                    showSearch
                    style={{ width: 100 }}
                    placeholder="Select a concept list"
                    optionFilterProp="children"
                    value={selectedConceptList}
                    onChange={setSelectedConceptList}
                    filterOption={(input, option) =>
                        option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                    }
                >
                    {props?.dataList?.map((item) => (
                        <Option
                            key={item.concept_list_id}
                            value={item.concept_list_id}
                        >
                            {item.name}
                        </Option>
                    ))}
                </Select>
            </div>
        </div>
    );

};
